import {
  Autocomplete,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import * as React from 'react'
import { Clear, Search } from '@mui/icons-material'
import {
  autoOptionsFromData,
  listItemsFromDataObjectArr,
} from '../../../Functions/ListItemGenerator'
import { useBioBankContext } from '../../../Contexts/Modules/BioBankModule'
import { tsLimsLine } from '../../../Typescript/StuartPy/Lims'
import { biobankItemCategory } from '../../../Typescript/TS_Types'
import { tsGetLimsSampleFilters } from '../../../Typescript/UI_Interfaces'
import { stackGap } from '../../../Constants&Theme/Spacing'
import { usePyGet } from '../../../APIs/Hooks/PyAPIHooks'

function SampleAsyncToolbar({
  apiFilters,
  setApiFilters,
  category,
}: {
  apiFilters: tsGetLimsSampleFilters
  setApiFilters: any
  category: biobankItemCategory
}) {
  const lineType = category === 'Researcher' ? 'my' : 'sale'
  const { ownersQuery } = useBioBankContext()
  const { sampleTypeQuery, descriptorQuery } = useBioBankContext()
  const { data: lines, isLoading: linesLoading } = usePyGet([
    '/lims/lines',
    `/${lineType}`,
  ])
  const sampleTypeOptions = listItemsFromDataObjectArr(
    (sampleTypeQuery.data ?? []).filter((t) => t.id !== 16), //BLOOD STU-1832
    'id',
    'id',
    'name',
    'name',
    sampleTypeQuery.isLoading,
  )
  const sexOptions = listItemsFromDataObjectArr(
    [
      {
        value: 'FEMALE',
        label: 'Female',
      },
      {
        value: 'MALE',
        label: 'Male',
      },
    ],
    'value',
    'value',
    'label',
    'value',
    false,
  )
  return (
    <Stack
      direction={'row'}
      gap={stackGap.row}
      width={1}
      mb={2}
    >
      {category === 'Researcher' && (
        <Autocomplete
          isOptionEqualToValue={(option, value) => {
            return option.value === value.value
          }}
          onChange={(event: any, newValue: any) => {
            setApiFilters((f: any) => {
              return {
                ...f,
                lims_owner_entity_id: newValue?.value ?? undefined,
                enabled: false,
              }
            })
          }}
          renderInput={(params) => (
            <TextField
              label={'Owner'}
              {...params}
            />
          )}
          loading={ownersQuery.isLoading}
          fullWidth
          options={autoOptionsFromData(
            ownersQuery.data,
            'OwnerID',
            'OwnerName',
          )}
        />
      )}
      <Autocomplete
        fullWidth
        autoHighlight
        onChange={(event, value) =>
          setApiFilters((f: any) => {
            return { ...f, short_line_name: value?.id, enabled: false }
          })
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={
          apiFilters?.short_line_name ?
            {
              id: apiFilters?.short_line_name,
              label: apiFilters?.short_line_name,
            }
          : null
        }
        options={
          lines ?
            lines
              .sort((a: any, b: any) => {
                return a.ShortLineName.localeCompare(b.ShortLineName)
              })
              .map((l: tsLimsLine) => {
                return { id: l.ShortLineName, label: l.ShortLineName }
              })
          : []
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Short Line Name"
          />
        )}
        loading={linesLoading}
      />
      <FormControl fullWidth>
        <InputLabel>Sex</InputLabel>
        <Select
          label="Sex"
          variant="outlined"
          value={apiFilters?.sex || ''}
          onChange={(e) =>
            setApiFilters((f: any) => {
              return { ...f, sex: e.target.value, enabled: false }
            })
          }
          endAdornment={
            apiFilters?.sex && (
              <IconButton
                onClick={() =>
                  setApiFilters((f: any) => {
                    return { ...f, sex: undefined, enabled: false }
                  })
                }
              >
                <Clear fontSize={'small'} />
              </IconButton>
            )
          }
        >
          {sexOptions}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Sample Type</InputLabel>{' '}
        <Select
          label="Sample Type"
          variant="outlined"
          value={apiFilters?.sample_type || ''}
          onChange={(e) =>
            setApiFilters((f: any) => {
              return { ...f, sample_type: e.target.value, enabled: false }
            })
          }
          endAdornment={
            apiFilters?.sample_type && (
              <IconButton
                onClick={() =>
                  setApiFilters((f: any) => {
                    return { ...f, sample_type: undefined, enabled: false }
                  })
                }
              >
                <Clear fontSize={'small'} />
              </IconButton>
            )
          }
        >
          {sampleTypeOptions}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Sample Details</InputLabel>{' '}
        <Select
          label="Sample Details"
          variant="outlined"
          value={apiFilters?.sample_detail || ''}
          onChange={(e) =>
            setApiFilters((f: any) => {
              return { ...f, sample_detail: e.target.value, enabled: false }
            })
          }
          endAdornment={
            apiFilters?.sample_detail && (
              <IconButton
                onClick={() =>
                  setApiFilters((f: any) => {
                    return { ...f, sample_detail: undefined, enabled: false }
                  })
                }
              >
                <Clear fontSize={'small'} />
              </IconButton>
            )
          }
        >
          {listItemsFromDataObjectArr(
            descriptorQuery.data ?? [],
            'CodeDescription',
            'CodeValue',
            'CodeDescription',
            'reqSortOrder',
            descriptorQuery.isLoading,
          )}
        </Select>
      </FormControl>
      <Button
        sx={{ minWidth: 120 }}
        startIcon={<Search />}
        variant={'contained'}
        disabled={
          apiFilters?.enabled ||
          !(
            !!apiFilters?.short_line_name ||
            !!apiFilters?.sex ||
            !!apiFilters?.sample_type ||
            !!apiFilters?.lims_owner_entity_id ||
            !!apiFilters?.sample_detail
          )
        }
        onClick={() => {
          setApiFilters((f: any) => {
            return { ...f, enabled: true }
          })
        }}
      >
        Search
      </Button>
    </Stack>
  )
}

export default SampleAsyncToolbar
